import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useGetAccountsQuery } from '@/pages/hainok/Accounts/accountsApi';
import { Search, ChevronLeft, ChevronRight, Building2, Users2, MoreHorizontal, Eye } from 'lucide-react';
import type { Account } from '@/types/accounts.types';
import { UsersDialog } from '@/pages/hainok/Accounts/components/users-dialog';
import { ActionsDialog } from '@/pages/hainok/Accounts/components/actions-dialog';

const limit = 10;

const formatDate = (date: Date | string) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'active':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'incomplete':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'canceled':
      return 'bg-red-100 text-red-800 border-red-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

export const Accounts = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const companyIdFromQuery = urlParams.get('companyId') ?? '';
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState(companyIdFromQuery);
  const [searchInput, setSearchInput] = useState(companyIdFromQuery);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [actionsDialogOpen, setActionsDialogOpen] = useState(false);
  const [usersDialogOpen, setUsersDialogOpen] = useState(false);

  const { data, isLoading, error } = useGetAccountsQuery({
    page: currentPage,
    limit,
    text: searchText || undefined,
  });

  const totalPages = data ? Math.ceil(data.totalAccounts / limit) : 0;

  const handleSearch = () => {
    setSearchText(searchInput);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleClearSearch = () => {
    setSearchText('');
    setSearchInput('');
    setCurrentPage(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;
    handleSearch();
  };

  const handleOpenActionsDialog = (account: Account) => {
    setSelectedAccountId(account._id);
    setActionsDialogOpen(true);
  };

  const handleOpenUsersDialog = (account: Account) => {
    setSelectedAccountId(account._id);
    setUsersDialogOpen(true);
  };

  return (
    <>
      {/* Accounts Overview */}
      <div className='mb-8'>
        <h2 className='text-3xl font-bold text-white mb-2'>Accounts Management</h2>
        <p className='text-slate-400'>Manage and monitor all accounts in your system.</p>
      </div>

      {/* Stats and Search */}
      <div className='grid gap-6 md:grid-cols-3 mb-6'>
        <Card className='bg-slate-800 border-slate-700'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-medium text-slate-400'>Total Accounts</p>
                <p className='text-3xl font-bold text-white'>{isLoading ? '...' : data?.totalAccounts || 0}</p>
              </div>
              <Building2 className='h-8 w-8 text-slate-400' />
            </div>
          </CardContent>
        </Card>

        <Card className='bg-slate-800 border-slate-700'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-medium text-slate-400'>Active Accounts</p>
                <p className='text-3xl font-bold text-white'>{isLoading ? '...' : (data?.activeAccounts ?? 0)}</p>
              </div>
              <Users2 className='h-8 w-8 text-slate-400' />
            </div>
          </CardContent>
        </Card>

        <Card className='bg-slate-800 border-slate-700'>
          <CardContent className='p-6'>
            <div className='flex items-center gap-3'>
              <Input
                placeholder='Search accounts...'
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
                className='bg-slate-700 border-slate-600 text-white placeholder:text-slate-400'
              />
              <Button onClick={handleSearch} size='sm' disabled={isLoading} className='flex-shrink-0'>
                <Search className='h-4 w-4' />
              </Button>
              {searchText && (
                <Button onClick={handleClearSearch} variant='outline' size='sm' className='flex-shrink-0'>
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Accounts Table */}
      <Card className='bg-slate-800 border-slate-700'>
        <CardHeader>
          <CardTitle className='text-white'>
            Accounts List
            {searchText && (
              <span className='text-sm font-normal text-slate-400 ml-2'>(filtered by "{searchText}")</span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className='flex items-center justify-center py-8'>
              <div className='text-slate-400'>Loading accounts...</div>
            </div>
          ) : error ? (
            <div className='flex items-center justify-center py-8'>
              <div className='text-red-400'>Error loading accounts</div>
            </div>
          ) : !data?.accounts?.length ? (
            <div className='flex items-center justify-center py-8'>
              <div className='text-slate-400'>
                {searchText ? 'No accounts found matching your search' : 'No accounts found'}
              </div>
            </div>
          ) : (
            <>
              {/* Table */}
              <div className='overflow-x-auto'>
                <table className='w-full'>
                  <thead>
                    <tr className='border-b border-slate-700'>
                      <th className='text-left py-3 px-2 text-slate-300 font-medium'>Name</th>
                      <th className='text-left py-3 px-2 text-slate-300 font-medium'>Subscription</th>
                      <th className='text-left py-3 px-2 text-slate-300 font-medium'>Product</th>
                      <th className='text-left py-3 px-2 text-slate-300 font-medium'>Status</th>
                      <th className='text-left py-3 px-2 text-slate-300 font-medium'>Type</th>
                      <th className='text-left py-3 px-2 text-slate-300 font-medium'>Customer ID</th>
                      <th className='text-left py-3 px-2 text-slate-300 font-medium'>Created</th>
                      <th className='text-left py-3 px-2 text-slate-300 font-medium'>Users</th>
                      <th className='text-left py-3 px-2 text-slate-300 font-medium'>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.accounts.map((account: Account) => (
                      <tr key={account._id} className='border-b border-slate-700/50 hover:bg-slate-700/25'>
                        <td className='py-4 px-2'>
                          <div>
                            <div className='text-white font-medium'>{account.name}</div>
                            <div className='text-sm text-slate-400'>{account._id}</div>
                          </div>
                        </td>
                        <td className='py-4 px-2'>
                          <div className='flex justify-center items-center flex-col gap-1 max-w-18'>
                            <Badge className={getStatusColor(account.subscriptionStatus)}>
                              {account.subscriptionStatus ?? 'not set'}
                            </Badge>
                          </div>
                        </td>
                        <td className='py-4 px-2'>
                          <div>
                            <div className='text-white font-medium'>{account.productName ?? 'Not set'}</div>
                          </div>
                        </td>
                        <td className='py-4 px-2'>
                          <div className='flex justify-center items-center flex-col gap-1 max-w-18'>
                            <Badge
                              variant='secondary'
                              className={`${account.isActive ? 'bg-green-500' : 'bg-red-900'} text-black`}
                            >
                              {account.isActive ? 'active' : 'inactive'}
                            </Badge>
                          </div>
                        </td>
                        <td className='py-4 px-2'>
                          <div className='text-slate-300'>
                            {account.isFreemium ? 'Freemium' : account.accountType || 'Standard'}
                          </div>
                        </td>
                        <td className='py-4 px-2'>
                          <div className='text-slate-300'>{account?.stripeCustomerId ?? 'Not Set'}</div>
                        </td>
                        <td className='py-4 px-2'>
                          <div className='text-slate-300'>{formatDate(account.createdAt)}</div>
                        </td>
                        <td className='py-4 px-2'>
                          <Button
                            variant='ghost'
                            size='sm'
                            onClick={() => handleOpenUsersDialog(account)}
                            className='text-slate-300 hover:text-white hover:bg-slate-700'
                          >
                            <Eye className='h-4 w-4 mr-1' />
                            {account.users?.length ?? 0}
                          </Button>
                        </td>
                        <td className='py-4 px-2'>
                          <Button
                            variant='ghost'
                            size='sm'
                            onClick={() => handleOpenActionsDialog(account)}
                            className='text-slate-300 hover:text-white hover:bg-slate-700'
                          >
                            <MoreHorizontal className='h-4 w-4' />
                            Actions
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className='flex items-center justify-between mt-6 pt-4 border-t border-slate-700'>
                  <div className='text-sm text-slate-400'>
                    Showing {(currentPage - 1) * limit + 1} to {Math.min(currentPage * limit, data.totalAccounts)} of{' '}
                    {data.totalAccounts} accounts
                  </div>
                  <div className='flex items-center gap-2'>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                      disabled={currentPage <= 1 || isLoading}
                    >
                      <ChevronLeft className='h-4 w-4' />
                      Previous
                    </Button>

                    <div className='flex items-center gap-1'>
                      {[...Array(totalPages)].map((_, i) => {
                        const page = i + 1;
                        if (page === 1 || page === totalPages || (page >= currentPage - 2 && page <= currentPage + 2)) {
                          return (
                            <Button
                              key={page}
                              variant={currentPage === page ? 'default' : 'outline'}
                              size='sm'
                              onClick={() => setCurrentPage(page)}
                              disabled={isLoading}
                              className='w-8 h-8 p-0'
                            >
                              {page}
                            </Button>
                          );
                        } else if (page === currentPage - 3 || page === currentPage + 3) {
                          return (
                            <span key={page} className='text-slate-400'>
                              ...
                            </span>
                          );
                        }
                        return null;
                      })}
                    </div>

                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                      disabled={currentPage >= totalPages || isLoading}
                    >
                      Next
                      <ChevronRight className='h-4 w-4' />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Actions Dialog */}
      <ActionsDialog
        actionsDialogOpen={actionsDialogOpen}
        setActionsDialogOpen={setActionsDialogOpen}
        selectedAccountForActions={data?.accounts?.find(({ _id }) => _id === selectedAccountId) || null}
      />

      {/* Users Dialog */}
      <UsersDialog
        users={data?.accounts?.find(({ _id }) => _id === selectedAccountId)?.users || []}
        usersDialogOpen={usersDialogOpen}
        setUsersDialogOpen={setUsersDialogOpen}
        accountId={selectedAccountId ?? ''}
      />
    </>
  );
};
